/**
 * The pure, tested surface for the sign-in-methods screen. Every fan-facing
 * string on this screen lives here. Straight apostrophes are deliberate and
 * match the design frame. No component types a copy string inline.
 */

export type typeSignInMethodKey = "apple" | "google" | "email";

export type typeSignInMethodsData = {
  email: { isLinked: boolean; address?: string | null };
  apple: { isLinked: boolean; linkedAt?: string | null };
  google: { isLinked: boolean; linkedAt?: string | null };
};

/** 44.1: "One card, three rows, in this order: Apple, Google, Email." */
export const SIGN_IN_METHOD_ORDER = ["apple", "google", "email"] as const;

export const SIGN_IN_METHODS_HEADING = "Sign-in methods";
export const SIGN_IN_METHODS_BACK_LABEL = "Back to sign-in methods";
export const SIGN_IN_METHODS_CONTEXT_LINE =
  "Any of these signs you into this account.";
export const KEEP_ONE_METHOD_LINE = "Keep at least one way to sign in.";
export const ADD_LABEL = "Add";
export const CHANGE_LABEL = "Change";
export const REMOVE_LABEL = "Remove";
export const CONNECTED_LABEL = "Connected";
export const NOT_CONNECTED_LABEL = "Not connected";
export const APPLE_ROW_LABEL = "Apple";
export const GOOGLE_ROW_LABEL = "Google";
export const EMAIL_ROW_LABEL = "Email";
export const EMAIL_NOT_ADDED_VALUE = "Not added";
export const EMAIL_FIELD_LABEL = "Email address";
export const CODE_FIELD_LABEL = "One-time code";
export const SEND_CODE_LABEL = "Send a sign-in code";
export const CONFIRM_CODE_LABEL = "Confirm";
export const EMAIL_TAKEN_INLINE_ERROR =
  "That email is already signed in on another account. Sign in to that account and delete it there if you want to free it up.";
export const PROVIDER_CONFLICT_BODY =
  "It's already the sign-in method for a different Sportsmet account. Sign in to that account instead, or delete it there to free this one up.";
export const PROVIDER_CONFLICT_PRIMARY_LABEL = "Try another account";
export const PROVIDER_CONFLICT_CANCEL_LABEL = "Cancel";
export const EMAIL_SHAPE_ERROR = "Enter an email address like you@example.com.";

/** The row's display label. */
export function methodRowLabel(method: typeSignInMethodKey): string {
  if (method === "apple") return APPLE_ROW_LABEL;
  if (method === "google") return GOOGLE_ROW_LABEL;
  return EMAIL_ROW_LABEL;
}

/**
 * The name this method goes by inside a sentence. Apple and Google are brands
 * and stay capitalized; email is a common noun and stays lowercase.
 */
export function methodSentenceName(method: typeSignInMethodKey): string {
  if (method === "apple") return "Apple";
  if (method === "google") return "Google";
  return "email";
}

export function isMethodLinked(
  data: typeSignInMethodsData | null | undefined,
  method: typeSignInMethodKey,
): boolean {
  if (!data) return false;
  return data[method].isLinked;
}

export function linkedMethodCount(
  data: typeSignInMethodsData | null | undefined,
): number {
  if (!data) return 0;
  return SIGN_IN_METHOD_ORDER.filter((method) => isMethodLinked(data, method))
    .length;
}

/**
 * 44.3 point 3 and S6. True only when this row is linked AND it is the one
 * and only linked method. The Remove action is not rendered when this is true.
 */
export function isOnlyRemainingMethod(
  data: typeSignInMethodsData | null | undefined,
  method: typeSignInMethodKey,
): boolean {
  return isMethodLinked(data, method) && linkedMethodCount(data) === 1;
}

/** Whether this browser can use Apple and Google sign-in right now. */
export type typeProviderAvailability = {
  isAppleAvailable: boolean;
  isGoogleAvailable: boolean;
};

/**
 * The value line under a row. Apple and Google always say Connected or Not
 * connected; email shows its address, or Not added when none is linked.
 */
export function methodRowValue(
  data: typeSignInMethodsData | null | undefined,
  method: typeSignInMethodKey,
): string {
  if (method === "email") {
    if (isMethodLinked(data, "email")) {
      return data?.email.address ?? EMAIL_NOT_ADDED_VALUE;
    }
    return EMAIL_NOT_ADDED_VALUE;
  }
  return isMethodLinked(data, method) ? CONNECTED_LABEL : NOT_CONNECTED_LABEL;
}

/**
 * The rows to draw, in SIGN_IN_METHOD_ORDER. A provider this browser cannot
 * use is hidden unless it is already linked, matching the onboarding rule in
 * apps/app/src/features/onboarding/signInMethodOffers.ts, ISSUE-1177.
 */
export function visibleSignInMethods(
  data: typeSignInMethodsData | null | undefined,
  availability: typeProviderAvailability,
): typeSignInMethodKey[] {
  return SIGN_IN_METHOD_ORDER.filter((method) => {
    if (method === "apple") {
      return isMethodLinked(data, "apple") || availability.isAppleAvailable;
    }
    if (method === "google") {
      return isMethodLinked(data, "google") || availability.isGoogleAvailable;
    }
    return true;
  });
}

/**
 * The shared "Any of these signs you into this account." line is drawn only
 * when an Apple or Google row is drawn.
 */
export function shouldShowContextLine(
  visible: readonly typeSignInMethodKey[],
): boolean {
  return visible.includes("apple") || visible.includes("google");
}

/** The other linked methods, in SIGN_IN_METHOD_ORDER, by sentence name. */
export function remainingMethodNames(
  data: typeSignInMethodsData | null | undefined,
  removing: typeSignInMethodKey,
): string[] {
  return SIGN_IN_METHOD_ORDER.filter(
    (method) => method !== removing && isMethodLinked(data, method),
  ).map((method) => methodSentenceName(method));
}

/**
 * "Apple" -> "Apple"; ["Apple","email"] -> "Apple and email". Never called
 * with an empty list, because the control that leads here is not rendered
 * when isOnlyRemainingMethod is true; returns "" defensively if it is.
 */
export function joinMethodNames(names: readonly string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0] ?? "";
  return names.join(" and ");
}

export function removeConfirmTitle(method: typeSignInMethodKey): string {
  return `Remove sign-in with ${methodSentenceName(method)}?`;
}

export function removeConfirmBody(
  data: typeSignInMethodsData | null | undefined,
  removing: typeSignInMethodKey,
): string {
  return `You'll still be able to sign in with ${joinMethodNames(remainingMethodNames(data, removing))}.`;
}

export function providerConflictTitle(provider: "apple" | "google"): string {
  if (provider === "apple") return "That Apple ID is already in use";
  return "That Google account is already in use";
}

/**
 * Same shape check sign-in.tsx uses at line 81. Copied, not imported: it is a
 * file-private const there and sign-in is not this plan's to modify.
 */
export function isEmailShapeValid(candidate: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate);
}
